import { useEffect, useState } from 'react';
import { EdBetween, EdGreaterThan, EdLessThan, FindManyReq } from '../../Entity/TypeormTypes';
import { Dayjs } from 'dayjs';
import { formatarData, formatarDinheiro } from '../../helper';
import { toast } from 'react-toastify';
import { Box, Grid, IconButton, LinearProgress, Paper, Typography } from '@mui/material';
import { useClienteLogado } from '../../redux/sessao/sessaoHooks';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import apiNotas from '../../api/apiNotas';
import { Nota } from '../../Entity/Nota';
import apiNotaServico from '../../api/apiNotaServico';
import Coluna from '../../Generic/EdTabela/Coluna';
import EdTabela from '../../Generic/EdTabela/EdTabela';

type Props = {
  dataInicio: Dayjs;
  dataFim: Dayjs;
  tipoNota: '55' | 'PS';
  buscarNota: boolean;
  onBuscaConcluida: () => void;
};

const ListaNotasClientePage: React.FC<Props> = (props) => {
  const { dataInicio, dataFim, tipoNota, buscarNota, onBuscaConcluida } = props;
  const [loading, setLoading] = useState(false);
  const [notas, setNotas] = useState<Nota[]>([]);
  const [query, setQuery] = useState<FindManyReq<Nota> | null>(null);
  const [pagina, setPagina] = useState(0);
  const linhasPorPagina = 10;
  const [totalLinhas, setTotalLinhas] = useState(0);
  const clienteLogado = useClienteLogado();

  useEffect(() => {
    if (!buscarNota) return;
    buscar();
    onBuscaConcluida();
  }, [buscarNota]);

  useEffect(() => {
    if (!query) return;
    getNotas();
  }, [query, pagina]);

  const getNotas = async () => {
    if (!query) {
      return;
    }

    try {
      setLoading(true);
      const queryFinal: FindManyReq<Nota> = {
        ...query,
        skip: pagina * linhasPorPagina,
        take: linhasPorPagina
      };
      const notas = await apiNotas.getMany(queryFinal);
      setNotas(notas);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      toast.error(error?.message || 'Erro ao buscar notas');
    }
  };

  const buscar = async () => {
    setNotas([]);
    setTotalLinhas(0);
    setPagina(0);
    const query: FindManyReq<Nota> = {};
    query.where = {};
    query.order = { numeroNf: 'DESC' };

    query.where.codigoCliente = clienteLogado?.codigo;
    query.where.codigoSit = '00';

    if (dataInicio && dataFim) {
      query.where.dataEmissao = EdBetween(
        `${dataInicio.format('YYYYMMDD')}`,
        `${dataFim.format('YYYYMMDD')}`
      );
    } else if (dataInicio) {
      query.where.dataEmissao = EdGreaterThan(`${dataInicio.format('YYYYMMDD')}`);
    } else if (dataFim) {
      query.where.dataEmissao = EdLessThan(`${dataFim.format('YYYYMMDD')}`);
    }

    query.where.modeloNf = tipoNota;

    try {
      setLoading(true);

      const totalLinhas = await apiNotas.count(query);

      setQuery({ ...query });
      setTotalLinhas(totalLinhas);
      setPagina(0);
    } catch (error: any) {
      setLoading(false);
      toast.error(error.data);
    }
  };

  const getPdfeNfe = async (nota: Nota) => {
    try {
      setLoading(true);

      const pdf = await apiNotas.gerarDanfeEdCliente({
        codigoEmpresa: nota.codigoEmpresa,
        numeroNf: nota.numeroNf,
        serieNf: nota.serieNf
      });

      const url = window.URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao gerar DANFE');
    } finally {
      setLoading(false);
    }
  };

  const abrirPdfNFSe = async (nota: Nota) => {
    try {
      const { codigoEmpresa, numeroNf, serieNf } = nota;
      const url = await apiNotaServico.getPdfNfseEdCliente({
        codigoEmpresa,
        numeroNf,
        serieNf
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      throw new Error('Erro ao imprimir nota fiscal.');
    }
  };

  const abrirPdf = async (nota: Nota) => {
    if (nota.modeloNf === '55') {
      const existe = await apiNotas.verificarXmlNotaEdCliente(nota);

      if (!existe.existe) {
        toast.error('XML não disponível para esta nota fiscal. Entre em contato com nossa equipe.');
        return;
      }

      return getPdfeNfe(nota);
    }

    if (nota.modeloNf === 'PS') {
      return abrirPdfNFSe(nota);
    }
  };

  const cabecalhoTabela = [
    new Coluna<Nota>({
      label: 'Numero',
      render: (nota) => {
        if (!nota.numeroNf) return '';
        return <p>{nota.numeroNf}</p>;
      },
      align: 'left'
    }),
    new Coluna<Nota>({
      label: 'Data',
      render: (nota) => formatarData(nota.dataEmissao),
      align: 'center'
    }),
    new Coluna<Nota>({
      label: '(R$) Valor',
      render: (nota) => {
        return (
          <div>
            <div>R$ {formatarDinheiro(nota.valorTotal)}</div>
          </div>
        );
      },
      align: 'right'
    }),
    new Coluna<Nota>({
      label: 'Visualizar',
      render: (nota) => {
        return (
          <>
            <IconButton onClick={() => abrirPdf(nota)} title="Visualizar DANFE">
              <RemoveRedEyeOutlinedIcon />
            </IconButton>
          </>
        );
      },
      align: 'right'
    })
  ];

  return (
    <Box>
      {loading ? (
        <Box>
          <Typography fontWeight="bold" variant="h6">
            Buscando informações . . .
          </Typography>
          <LinearProgress />
        </Box>
      ) : (
        <Grid item xs={12}>
          <EdTabela
            chave={'TabelaEdClienteListaNotas'}
            totalLinhas={totalLinhas}
            pagina={pagina}
            cabecalhoTabela={cabecalhoTabela}
            linhas={notas}
            loading={loading}
            linhasPorPagina={Math.min(totalLinhas, linhasPorPagina)}
            mudarPagina={setPagina}
            compactar
          />
        </Grid>
      )}
    </Box>
  );
};

export default ListaNotasClientePage;
